import { inject, Service } from '@angular/core';
import {GuruEntity} from '../model/guru-entity';
import {forkJoin, Observable, Subject} from 'rxjs';
import {ModelService} from '../services/model-service';
import {SelectionUtils} from './selection-utils';
import {UnitConversionUtils} from './unit-conversion-utils';

@Service()
export class UnitConversionService {
  private readonly modelService = inject(ModelService);


  convertMeasurementUnits(engineKey: string, selections: GuruEntity[]): Observable<GuruEntity[]> {
    const subject = new Subject<GuruEntity[]>();
    this.modelService.getEntity(engineKey, 'USMetricInput').subscribe(metricInput => {
      const isMetric = metricInput && 'true' === metricInput.Value;
      const valueSetters: Observable<string>[] = [];
      const airflow = this.getNumericSelectionValue(selections, 'USFAirflow');
      if (airflow > 0) {
        const newValue = UnitConversionUtils.convertAirflow(airflow, isMetric);
        valueSetters.push(this.modelService.setValue('USFAirflow', engineKey, newValue));
      }
      valueSetters.push(this.modelService.setValue('USCEDB', engineKey, this.convertTemperature(selections, 'USCEDB', isMetric)));
      valueSetters.push(this.modelService.setValue('USCEWB', engineKey, this.convertTemperature(selections, 'USCEWB', isMetric)));
      valueSetters.push(this.modelService.setValue('USCEWT', engineKey, this.convertTemperature(selections, 'USCEWT', isMetric)));
      valueSetters.push(this.modelService.setValue('USHEDB', engineKey, this.convertTemperature(selections, 'USHEDB', isMetric)));
      valueSetters.push(this.modelService.setValue('USHEWT', engineKey, this.convertTemperature(selections, 'USHEWT', isMetric)));
      const esp = this.getNumericSelectionValue(selections, 'USFESP');
      if (esp > 0) {
        const newValue = UnitConversionUtils.convertStaticPressure(esp, isMetric);
        valueSetters.push(this.modelService.setValue('USFESP', engineKey, newValue));
      }
      valueSetters.push(this.modelService.setValue('USAltitude', engineKey, this.convertAltitude(selections, isMetric)));
      this.addTemperatureSetterIfPresent(valueSetters, selections, 'USCWTC', engineKey, isMetric);
      this.addTemperatureSetterIfPresent(valueSetters, selections, 'USHWTC', engineKey, isMetric);
      this.addFlowRateSetterIfPresent(valueSetters, selections, 'USCWFR', engineKey, isMetric);
      this.addFlowRateSetterIfPresent(valueSetters, selections, 'USHWFR', engineKey, isMetric);
      forkJoin(valueSetters).subscribe(() => {
        this.modelService.getEntitiesStartingWithName(engineKey, 'US').subscribe(userSelections => {
          subject.next(userSelections);
        }, error => subject.error(error));
      }, error => subject.error(error));
    }, error => subject.error(error));
    return subject.asObservable();
  }

  private addFlowRateSetterIfPresent(setters: Observable<string>[], selections: GuruEntity[], entityName: string, engineKey: string,
                                     isMetric: boolean): void {
    const currentValue = this.getNumericSelectionValue(selections, entityName);
    if (currentValue > 0) {
      const newValue = isMetric ? '0.2' : '3';
      setters.push(this.modelService.setValue(entityName, engineKey, newValue));
    }
  }

  private addTemperatureSetterIfPresent(setters: Observable<string>[], selections: GuruEntity[], entityName: string, engineKey: string,
                                        isMetric: boolean): void {
    const currentValue = this.getNumericSelectionValue(selections, entityName);
    if (currentValue > 0) {
      let newValue: string;
      if ('USHWTC' === entityName) {
        newValue = isMetric ? '17' : '30';
      } else {
        newValue = isMetric ? '5.6' : '10';
      }
      setters.push(this.modelService.setValue(entityName, engineKey, newValue));
    }
  }

  private convertAltitude(selections: GuruEntity[], isMetric: boolean): string {
    const currentValue = this.getNumericSelectionValue(selections, 'USAltitude');
    return UnitConversionUtils.convertAltitudeValue(currentValue, isMetric);
  }

  private convertTemperature(selections: GuruEntity[], entityName: string, isMetric: boolean): string {
    const currentValue = this.getNumericSelectionValue(selections, entityName);
    return UnitConversionUtils.convertTemperatureValue(currentValue, isMetric);
  }

  private getNumericSelectionValue(selectionEntities: GuruEntity[], entityName: string): number {
    const entity = SelectionUtils.getEntity(selectionEntities, entityName);
    return (entity?.Value || '') === '' ? 0 : +entity.Value.toString();
  }

}
